import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Filter,
  GetCallsRequest,
  GetCallsResponse,
} from '@weave/schema-gen-ts/dist/schemas/call-intelligence/v2/call_intelligence.pb';
import { SortType, Sentiment, Category } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { Call } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb.js';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb.js';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import utc from 'dayjs/plugin/utc';
import { InfiniteData, UseInfiniteQueryResult, useQueryClient } from 'react-query';
import { CALL_INTEL_CALL_STATUSES, CallIntelTypes, useListCallsQuery } from '@frontend/api-call-intel';
import { PanelEvents, useCallInsightController } from '@frontend/call-insight-panel';
import { useHasCallIntelAclAccess } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { CallInsightFilter } from '../types';

dayjs.extend(utc);
dayjs.extend(isSameOrAfter);

// Constants for pagination and sorting
const PAGE_LIMIT = 25;
const SORT_ORDER = SortType.SORT_DESC;
const API_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss.SSS';

// Types for date range and API response
type DateRange = { start: Dayjs; end: Dayjs };
type HistoryPage = { rows: Call[] };

// State type for managing buffered calls and pagination
type CallInsightState = {
  bufferedCalls: Call[]; // Stores all fetched calls to avoid duplicate API calls
  activePeriodIndex: number; // Tracks the current period being displayed
  isCurrentPeriodComplete: boolean; // Indicates if the current period has been fully loaded
};

// Combined type for the return value of the hook
type UseCallInsightHistoryResult = UseInfiniteQueryResult<HistoryPage> & {
  visiblePeriod: DateRange; // The time period visible to the user (e.g., current period start to today)
  visibleCalls: Call[]; // Calls that fall within the visible period
  canLoadMore: boolean; // Indicates if more data can be loaded
  isCurrentPeriodComplete: boolean; // Indicates if the current period has been fully loaded
  hasLoadedPreviousPeriods: boolean; // Indicates if data for previous periods has been loaded
};

type UseCallInsightHistoryParams = {
  personId: string;
  filters: CallInsightFilter[];
};

export const useCallInsightHistory = ({
  personId,
  filters,
}: UseCallInsightHistoryParams): UseCallInsightHistoryResult => {
  const alert = useAlert();
  const { t } = useTranslation('callIntelInsights');
  const { selectedLocationIds } = useAppScopeStore();
  const queryClient = useQueryClient();
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isServiceQualityEnabled = getFeatureFlagValue('enable-service-quality');
  const hasServiceQualityAccess = useHasCallIntelAclAccess([Permission.CALL_INTELLIGENCE_READ_SERVICE_QUALITY]);

  const { onEvent, offEvent } = useCallInsightController({
    source: 'callHistoryContactPanel',
  });

  // Define the fixed API date range (from 2024-01-01 as call-intel was released on that date)
  const apiDateRange = useMemo(
    () => ({
      start: dayjs.utc('2024-01-01'),
      end: dayjs.utc(),
    }),
    []
  );

  // State to manage buffered calls and pagination
  const [callInsightState, setCallInsightState] = useState<CallInsightState>({
    bufferedCalls: [],
    activePeriodIndex: 0,
    isCurrentPeriodComplete: false,
  });

  const { bufferedCalls, activePeriodIndex, isCurrentPeriodComplete } = callInsightState;

  // Build the API request with filters and pagination
  const apiRequest = useMemo<GetCallsRequest>(() => {
    const filterMap: Record<number, Filter> = {
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_DATE_RANGE]: {
        dateRange: {
          start: apiDateRange.start.format(API_DATE_FORMAT),
          end: apiDateRange.end.format(API_DATE_FORMAT),
        },
      },
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_LOCATION]: {
        locations: selectedLocationIds,
      },
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_CALL_STATUS]: {
        callStatus: [...CALL_INTEL_CALL_STATUSES],
      },
      [CallIntelTypes.FilterTypeEnum.FILTER_BY_PERSON_ID]: {
        personId: [personId],
      },
    };

    const sentimentFilters = filters
      .filter((filter) => filter.filterType === 'sentiment')
      .map((filter) => filter.key as Sentiment);
    if (sentimentFilters.length) {
      filterMap[CallIntelTypes.FilterTypeEnum.FILTER_BY_SENTIMENT] = { sentiments: sentimentFilters };
    }

    const categoryFilters = filters
      .filter((filter) => filter.filterType === 'category')
      .map((filter) => filter.key as Category);
    if (categoryFilters.length) {
      filterMap[CallIntelTypes.FilterTypeEnum.FILTER_BY_CATEGORY] = { categories: categoryFilters };
    }

    return {
      limit: PAGE_LIMIT,
      offset: bufferedCalls.length,
      sortType: SORT_ORDER,
      filter: filterMap,
      includesHydration: [CallIntelTypes.IncludesHydration.HYDRATE_OFFICE_USER],
      includesAnalysis: [
        CallIntelTypes.IncludesAnalysis.CALL_CATEGORY,
        CallIntelTypes.IncludesAnalysis.CALL_SENTIMENT,
        CallIntelTypes.IncludesAnalysis.FOLLOWUP_TASKS,
        CallIntelTypes.IncludesAnalysis.CALL_APPOINTMENT_TYPE, //We are adding appointment type here coz when clicked on call-intel history item , we dont want to call hydrated-call-record to fetch with call-intel data juts because appointment type is not there,
        ...(isServiceQualityEnabled && hasServiceQualityAccess
          ? [CallIntelTypes.IncludesAnalysis.CALL_SERVICE_QUALITY]
          : []),
        CallIntelTypes.IncludesAnalysis.CALL_APPOINTMENTS,
      ],
    };
  }, [
    apiDateRange.start,
    apiDateRange.end,
    selectedLocationIds,
    personId,
    filters,
    bufferedCalls.length,
    isServiceQualityEnabled,
    hasServiceQualityAccess,
  ]);

  const queryResult = useListCallsQuery({
    request: apiRequest,
    requestKeysToOmitFromQueryKey: ['offset'],
    options: {
      keepPreviousData: true,
      enabled: true,
      onError: () => alert.error(t("Couldn't load call list calls. Please try again.")),
      getNextPageParam: (lastPage) => {
        return lastPage.calls?.length === PAGE_LIMIT
          ? { offset: bufferedCalls.length + PAGE_LIMIT, limit: PAGE_LIMIT, lastRecordCursor: lastPage.nextCursor }
          : undefined;
      },
    },
  });

  // Append newly fetched calls into the buffer
  useEffect(() => {
    if (!queryResult.data) return;

    const newCalls = queryResult.data.pages.flatMap((page) => page.calls ?? []);
    setCallInsightState((prevState) => {
      const existingCallIds = new Set(prevState.bufferedCalls.map((call) => call.id));
      const uniqueNewCalls = newCalls.filter((call) => !existingCallIds.has(call.id));
      return {
        ...prevState,
        bufferedCalls: [...prevState.bufferedCalls, ...uniqueNewCalls],
      };
    });
  }, [queryResult.data]);

  // Compute the date range for the current period (12-month slices)
  const activePeriodRange = useMemo(
    () => ({
      start: dayjs().subtract(activePeriodIndex + 1, 'year'),
      end: dayjs().subtract(activePeriodIndex, 'year'),
    }),
    [activePeriodIndex]
  );

  // Compute the visible period (current period start to today)
  const visiblePeriod = useMemo<DateRange>(
    () => ({
      start: activePeriodRange.start,
      end: apiDateRange.end,
    }),
    [activePeriodRange.start, apiDateRange.end]
  );

  // Filter buffered calls to show only those in the current period
  const visibleCalls = useMemo(() => {
    const cutoffDate = activePeriodRange.start;
    return bufferedCalls.filter((call) => dayjs(call.startTime).isSameOrAfter(cutoffDate, 'day'));
  }, [bufferedCalls, activePeriodRange]);

  // Check if the current period is complete
  useEffect(() => {
    const isComplete =
      activePeriodIndex === 0 &&
      bufferedCalls.some((call) => dayjs(call.startTime).isBefore(activePeriodRange.start, 'day'));

    if (isComplete !== isCurrentPeriodComplete) {
      setCallInsightState((prevState) => ({ ...prevState, isCurrentPeriodComplete: isComplete }));
    }
  }, [bufferedCalls, activePeriodRange.start, activePeriodIndex, isCurrentPeriodComplete]);

  // Determine if more data is available
  const canLoadMore = useMemo(() => {
    return queryResult.hasNextPage || (activePeriodIndex === 0 && isCurrentPeriodComplete);
  }, [queryResult.hasNextPage, activePeriodIndex, isCurrentPeriodComplete]);

  const handleUpdateCallAnalysis = useCallback(
    ({ call: newSelectedCall }: PanelEvents['callAnalysisUpdated']) => {
      queryClient.setQueryData<InfiniteData<GetCallsResponse> | undefined>(queryResult.queryKey, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            calls: page.calls?.map((call) => (call.id === newSelectedCall.id ? (newSelectedCall as Call) : call)),
          })),
        };
      });

      setCallInsightState((prev) => ({
        ...prev,
        bufferedCalls: prev.bufferedCalls.map((call) =>
          call.id === newSelectedCall.id ? (newSelectedCall as Call) : call
        ),
      }));
    },
    [queryClient, queryResult.queryKey]
  );

  useEffect(() => {
    onEvent('callAnalysisUpdated', handleUpdateCallAnalysis);

    return () => {
      offEvent('callAnalysisUpdated', handleUpdateCallAnalysis);
    };
  }, [handleUpdateCallAnalysis, offEvent, onEvent]);

  // Fetch the next page or move to the next period
  const fetchNextPage = useCallback(() => {
    if (queryResult.hasNextPage) {
      return queryResult.fetchNextPage();
    }
    setCallInsightState((prevState) => ({
      ...prevState,
      activePeriodIndex: prevState.activePeriodIndex + 1,
    }));
    return Promise.resolve();
  }, [queryResult]);

  // Shape the data for the infinite query result
  const pages: HistoryPage[] = useMemo(() => [{ rows: visibleCalls }], [visibleCalls]);

  const isInitialHistoryLoading = queryResult.isLoading || (queryResult.isSuccess && bufferedCalls.length === 0);

  return useMemo(
    () =>
      ({
        ...queryResult,
        data: { pages, pageParams: queryResult.data?.pageParams ?? [] },
        isLoading: isInitialHistoryLoading,
        fetchNextPage,
        visiblePeriod,
        canLoadMore,
        isCurrentPeriodComplete,
        hasLoadedPreviousPeriods: activePeriodIndex > 0,
        visibleCalls,
      } as UseCallInsightHistoryResult),
    [
      activePeriodIndex,
      queryResult,
      pages,
      fetchNextPage,
      visiblePeriod,
      visibleCalls,
      canLoadMore,
      isCurrentPeriodComplete,
      isInitialHistoryLoading,
    ]
  );
};
